import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { SectionTitle, TotalContainer, Campo } from './styles'
import { ButtonSalvar } from '../ButtonsCrud/style'
import axios from '../../../services/withAxios'
import Produto from '../../../interfaces/produto'
import ProdutoNotaDevolucao from '../../../interfaces/produtoNotaDevolucao'

interface ProdutoOption {
  id: string
  label: string
  produto: Produto
}

interface NotaFiscalProdutosProps {
  produtos: ProdutoNotaDevolucao[]
  onProdutosChange: (produtos: ProdutoNotaDevolucao[]) => void
  somenteLeitura?: boolean
  produtosDisponiveis?: ProdutoNotaDevolucao[]
}

const criarOption = (produto: ProdutoNotaDevolucao): ProdutoOption => ({
  id: produto.id || produto.codigo || produto.nomeOriginal,
  label: produto.nomeOriginal || '',
  produto
})

const NotaFiscalProdutos: React.FC<NotaFiscalProdutosProps> = ({
  produtos,
  onProdutosChange,
  somenteLeitura = false,
  produtosDisponiveis
}) => {
  const [produtoDialogAberto, setProdutoDialogAberto] = useState<boolean>(false)
  const [produtoLookup, setProdutoLookup] = useState<string>('')
  const [produtoSelecionado, setProdutoSelecionado] = useState<ProdutoOption | null>(null)
  const [produtoOptions, setProdutoOptions] = useState<ProdutoOption[]>([])
  const [produtoCodigo, setProdutoCodigo] = useState<string>('')
  const [produtoCodigoBarras, setProdutoCodigoBarras] = useState<string>('')
  const [produtoNcm, setProdutoNcm] = useState<string>('')
  const [produtoCfop, setProdutoCfop] = useState<string>('')
  const [produtoQuantidade, setProdutoQuantidade] = useState<string>('1')
  const [produtoValorUnitario, setProdutoValorUnitario] = useState<string>('0')
  const [produtoBuscando, setProdutoBuscando] = useState<boolean>(false)

  const usarListaLocal = Array.isArray(produtosDisponiveis)

  const filterProdutoOptions = (options: ProdutoOption[], lookup: string) => {
    const pesquisa = lookup.trim().toUpperCase()

    if (!pesquisa) {
      return options
    }

    return options.filter((option) => {
      const nome = option.label.toUpperCase()
      const codigo = (option.produto.codigo || '').toUpperCase()
      const codigoDeBarra = (option.produto.codigoDeBarra || '').toUpperCase()

      return nome.includes(pesquisa) || codigo.includes(pesquisa) || codigoDeBarra.includes(pesquisa)
    })
  }

  const limparFormularioProduto = () => {
    setProdutoLookup('')
    setProdutoSelecionado(null)
    setProdutoCodigo('')
    setProdutoCodigoBarras('')
    setProdutoNcm('')
    setProdutoCfop('')
    setProdutoQuantidade('1')
    setProdutoValorUnitario('0')
    setProdutoBuscando(false)
  }

  const handleAbrirDialogProduto = () => {
    limparFormularioProduto()

    if (usarListaLocal) {
      setProdutoOptions((produtosDisponiveis || []).map(criarOption))
    } else {
      setProdutoOptions([])
    }

    setProdutoDialogAberto(true)
  }

  const handleFecharDialogProduto = () => {
    setProdutoDialogAberto(false)
  }

  const preencherCamposProduto = (produto: ProdutoNotaDevolucao | null) => {
    if (!produto) {
      setProdutoCodigo('')
      setProdutoCodigoBarras('')
      setProdutoNcm('')
      setProdutoCfop('')
      setProdutoValorUnitario('0')
      return
    }

    setProdutoCodigo((produto.codigo || '').toUpperCase())
    setProdutoCodigoBarras(String(produto.codigoDeBarra || ''))
    setProdutoNcm(produto.ncm || '')
    setProdutoCfop(String(produto.cfop || ''))
    setProdutoValorUnitario(String(produto.valorCompra || 0))
  }

  useEffect(() => {
    if (!produtoDialogAberto || usarListaLocal) {
      return
    }

    const pesquisa = produtoLookup.trim()

    if (!pesquisa) {
      setProdutoOptions([])
      setProdutoBuscando(false)
      return
    }

    let ativo = true
    setProdutoBuscando(true)

    const timeout = setTimeout(async () => {
      try {
        const resposta = await axios.get(`/produto/search/${pesquisa}`, {
          params: {
            avoidLoadingScreen: true
          }
        })

        if (!ativo) {
          return
        }

        const listaProdutos = (resposta.data || []) as Produto[]
        setProdutoOptions(listaProdutos.map(criarOption))
      } finally {
        if (ativo) {
          setProdutoBuscando(false)
        }
      }
    }, 300)

    return () => {
      ativo = false
      clearTimeout(timeout)
    }
  }, [produtoDialogAberto, produtoLookup, usarListaLocal])

  useEffect(() => {
    if (!produtoDialogAberto || !usarListaLocal) {
      return
    }

    setProdutoOptions((produtosDisponiveis || []).map(criarOption))
  }, [produtoDialogAberto, produtosDisponiveis, usarListaLocal])

  const handleAdicionarProduto = () => {
    const codigoBarras = produtoCodigoBarras.trim()
    const ncm = produtoNcm.trim()
    const cfop = Number(produtoCfop)
    const quantidade = Number(produtoQuantidade)
    const valorUnitario = Number(produtoValorUnitario)

    if (
      !codigoBarras
      || !ncm
      || !Number.isInteger(Number(ncm))
      || Number(ncm) <= 0
      || !Number.isInteger(cfop)
      || cfop <= 0
      || quantidade <= 0
      || valorUnitario <= 0
    ) {
      return
    }

    const produtoOriginal = produtoSelecionado?.produto as ProdutoNotaDevolucao | undefined
    const nomeOriginal = produtoOriginal?.nomeOriginal || produtoOriginal?.nome || ''

    onProdutosChange([
      ...produtos,
      {
        ...(produtoOriginal || ({} as ProdutoNotaDevolucao)),
        codigo: produtoCodigo.toUpperCase().trim(),
        codigoDeBarra: codigoBarras.toUpperCase(),
        ncm,
        nomeOriginal,
        numeroItem: produtoOriginal?.numeroItem ?? produtos.length + 1,
        quantidade,
        cfop,
        valor: valorUnitario,
        icms: produtoOriginal?.icms ?? {
          origem: 0,
          cst: '00',
          vBC: 0,
          pICMS: 0,
          vICMS: 0
        },
        ipi: produtoOriginal?.ipi ?? {
          cst: '99',
          cEnq: '999',
          vBC: 0,
          pIPI: 0,
          vIPI: 0
        },
        pis: produtoOriginal?.pis ?? {
          cst: '99',
          vBC: 0,
          pPIS: 0,
          vPIS: 0
        },
        cofins: produtoOriginal?.cofins ?? {
          cst: '99',
          vBC: 0,
          pCOFINS: 0,
          vCOFINS: 0
        }
      }
    ])

    handleFecharDialogProduto()
    limparFormularioProduto()
  }

  const handleRemoverProduto = (index: number) => {
    onProdutosChange(produtos.filter((_, itemIndex) => itemIndex !== index))
  }

  const valorTotal = useMemo(() => {
    return produtos.reduce((acumulado, produto) => acumulado + (produto.quantidade * (produto.valor || 0)), 0)
  }, [produtos])

  const noOptionsText = usarListaLocal
    ? 'Nenhum produto disponível'
    : (produtoLookup.trim() ? 'Nenhum produto encontrado' : 'Digite para buscar produtos')

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <SectionTitle style={{ marginBottom: 0 }}>Produtos</SectionTitle>
        {!somenteLeitura && (
          <IconButton color="primary" onClick={handleAbrirDialogProduto} aria-label="Adicionar produto">
            <AddCircleIcon />
          </IconButton>
        )}
      </Stack>

      <TableContainer
        component={Paper}
        sx={{
          mt: 1,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          opacity: somenteLeitura ? 0.6 : 1
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>cEAN</TableCell>
              <TableCell>NCM</TableCell>
              <TableCell>CFOP</TableCell>
              <TableCell align="right">Qtd</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell align="right">Subtotal</TableCell>
              {!somenteLeitura && <TableCell align="center">Ações</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {produtos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={somenteLeitura ? 7 : 8} align="center">
                  Nenhum produto adicionado.
                </TableCell>
              </TableRow>
            ) : (
              produtos.map((produto, index) => (
                <TableRow key={`${produto.codigo}-${produto.cfop}-${index}`}>
                  <TableCell>{produto.codigo}</TableCell>
                  <TableCell>{produto.codigoDeBarra || '-'}</TableCell>
                  <TableCell>{produto.ncm || '-'}</TableCell>
                  <TableCell>{produto.cfop}</TableCell>
                  <TableCell align="right">{produto.quantidade}</TableCell>
                  <TableCell align="right">
                    {(produto.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </TableCell>
                  <TableCell align="right">
                    {(produto.quantidade * (produto.valor || 0)).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })}
                  </TableCell>
                  {!somenteLeitura && (
                    <TableCell align="center">
                      <IconButton color="error" onClick={() => handleRemoverProduto(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TotalContainer sx={{ opacity: somenteLeitura ? 0.6 : 1 }}>
        Total:&nbsp;
        {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
      </TotalContainer>

      <Dialog open={produtoDialogAberto} onClose={handleFecharDialogProduto} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Produto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              id="produtoId"
              options={produtoOptions}
              value={produtoSelecionado}
              inputValue={produtoLookup}
              autoComplete
              clearText="Limpar"
              closeText="Fechar"
              openText="Abrir"
              loading={produtoBuscando}
              noOptionsText={noOptionsText}
              filterOptions={(options, state) => filterProdutoOptions(options as ProdutoOption[], state.inputValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(_, newInputValue) => {
                setProdutoLookup(newInputValue)
              }}
              onChange={(_, produtoOption: ProdutoOption | null) => {
                setProdutoSelecionado(produtoOption)
                preencherCamposProduto(produtoOption?.produto as ProdutoNotaDevolucao | null)
              }}
              getOptionLabel={(option: ProdutoOption) => option.label}
              renderInput={(params) => (
                <Campo
                  {...params}
                  label="Produto"
                  autoFocus
                />
              )}
            />

            <Campo
              label="Código"
              value={produtoCodigo}
              onChange={(event) => setProdutoCodigo(event.target.value)}
              inputProps={{ style: { textTransform: 'uppercase' } }}
              fullWidth
            />
            <Campo
              label="cEAN (Código de Barras)"
              value={produtoCodigoBarras}
              onChange={(event) => setProdutoCodigoBarras(event.target.value)}
              inputProps={{ style: { textTransform: 'uppercase' } }}
              required
              fullWidth
            />
            <Campo
              label="NCM"
              type="number"
              value={produtoNcm}
              onChange={(event) => setProdutoNcm(event.target.value)}
              inputProps={{ min: 1, step: 1 }}
              required
              fullWidth
            />
            <Campo
              label="CFOP"
              type="number"
              value={produtoCfop}
              onChange={(event) => setProdutoCfop(event.target.value)}
              inputProps={{ min: 1, step: 1 }}
              required
              fullWidth
            />
            <Campo
              label="Quantidade"
              type="number"
              value={produtoQuantidade}
              onChange={(event) => setProdutoQuantidade(event.target.value)}
              inputProps={{ min: 1 }}
              required
              fullWidth
            />
            <Campo
              label="Valor"
              type="number"
              value={produtoValorUnitario}
              onChange={(event) => setProdutoValorUnitario(event.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharDialogProduto}>Cancelar</Button>
          <ButtonSalvar
            onClick={handleAdicionarProduto}
            disabled={
              !Number.isInteger(Number(produtoNcm))
              || Number(produtoNcm) <= 0
              || !produtoCodigoBarras.trim()
              || !Number.isInteger(Number(produtoCfop))
              || Number(produtoCfop) <= 0
              || Number(produtoQuantidade) <= 0
              || Number(produtoValorUnitario) <= 0
            }
          >
            Salvar
          </ButtonSalvar>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default NotaFiscalProdutos
